import { isNavSection, NavConfigEntry, NavItem, NavSection } from '@zag/ui';
import type { FeatureKey } from '@zag/licensing/common';

/**
 * Modules hidden across the whole UI (main nav, settings, topbar) while
 * still being finished — flip to `true` to bring a module back everywhere
 * at once. Independent of per-tenant licensing (`EntitlementsService`),
 * which stays server-driven; these are a blanket dev-time override read
 * by `navigation.config.ts`, `settings.component.ts`, and `layout.component.ts`.
 */
export const TIMETRACKING_MENU_ENABLED = false;
export const PROJECTS_MENU_ENABLED = false;
export const REPORTS_MENU_ENABLED = false;

/**
 * Maps nav item ids to the licensable module that must be active for the
 * item to appear. Ids without an entry are always shown (core features).
 * Filtering happens in layout.component.ts next to the permission filter.
 */
export const NAV_FEATURE_MAP: Partial<Record<string, FeatureKey>> = {
  'time-tracking-my-bookings': 'timetracking',
  approvals: 'timetracking',
  documents: 'documents',
  'planning-periods': 'planning',
  'my-plan': 'planning',
  'shift-applications': 'planning',
  'shift-requests': 'planning',
  'projects-time': 'projects',
  'projects-admin': 'projects',
  'projects-activity-types': 'projects',
  'projects-cost-rates': 'projects',
};

// "Deine Woche" is coupled to OWN time tracking, not to a role (story 35-2,
// U6): any signed-in person with a linked core.persons row and the base
// permission sees it — including working planners/admins.
const TIME_TRACKING_MY_BOOKINGS_ITEM: NavItem = {
  id: 'time-tracking-my-bookings',
  label: 'navigation.myBookings',
  icon: 'timer',
  route: '/time-tracking/my-bookings',
};

// Project time capture (spec project-time-tracking, story 8): like
// "Deine Woche" this is coupled to OWN bookings — any person-linked user
// with the self-service permission sees it, regardless of role.
const PROJECTS_TIME_ITEM: NavItem = {
  id: 'projects-time',
  label: 'navigation.projectTime',
  icon: 'work_history',
  route: '/projects',
};

// Configuration entries (rules, absence types, balance accounts) live under
// Settings → Time Tracking; only operational views remain here.
const TIME_TRACKING_SECTION: NavSection = {
  label: 'navigation.sectionTimeTracking',
  roles: ['planner', 'admin'],
  items: [
    {
      id: 'time-tracking-balance-overview',
      label: 'navigation.balanceOverview',
      icon: 'account_balance',
      route: '/time-tracking/balance-overview',
      roles: ['planner', 'admin'],
    },
  ],
};

// Projects section (spec project-time-tracking, story 8).
const PROJECTS_SECTION: NavSection = {
  label: 'navigation.sectionProjects',
  roles: ['planner', 'admin'],
  items: [
    {
      id: 'projects-admin',
      label: 'navigation.projectsAdmin',
      icon: 'folder',
      route: '/projects/admin',
      roles: ['planner', 'admin'],
    },
    {
      id: 'projects-activity-types',
      label: 'navigation.projectActivityTypes',
      icon: 'category',
      route: '/projects/admin/activity-types',
      roles: ['planner', 'admin'],
    },
    {
      id: 'projects-cost-rates',
      label: 'navigation.projectCostRates',
      icon: 'payments',
      route: '/projects/admin/cost-rates',
      roles: ['planner', 'admin'],
    },
  ],
};

const REPORTS_ITEM: NavItem = {
  id: 'reports',
  label: 'navigation.reports',
  icon: 'bar_chart',
  route: '/reports',
  roles: ['planner', 'admin'],
};

/**
 * SmartCapacity Navigation Configuration
 *
 * Defines the main navigation structure using a flat section-based layout.
 * Primary items (no section label) appear first; sections group related items
 * under a display-only heading.
 *
 * Permission filtering is done in layout.component.ts before passing entries
 * to the sidebar/mobile-dialog components.
 */
export const NAV_CONFIG: NavConfigEntry[] = [
  // ─── Primary items (always visible, no section) ──────────────────────────
  {
    id: 'dashboard',
    label: 'navigation.dashboard',
    icon: 'dashboard',
    route: '/dashboard',
  },
  {
    id: 'my-plan',
    label: 'navigation.myPlan',
    icon: 'calendar_view_week',
    route: '/my-plan',
    roles: ['employee'],
  },
  {
    id: 'shift-applications',
    label: 'navigation.openShifts',
    icon: 'event_available',
    route: '/shift-applications',
    roles: ['employee'],
  },
  {
    id: 'shift-requests',
    label: 'navigation.requests',
    icon: 'swap_horiz',
    route: '/shift-requests',
    roles: ['employee'],
  },
  ...(TIMETRACKING_MENU_ENABLED ? [TIME_TRACKING_MY_BOOKINGS_ITEM] : []),
  ...(PROJECTS_MENU_ENABLED ? [PROJECTS_TIME_ITEM] : []),
  {
    id: 'calendars',
    label: 'navigation.calendar',
    icon: 'calendar_today',
    route: '/calendars',
  },
  // Central approvals entry point (story 34-1, F14): generic top-level item —
  // today fed by timetracking, later by further modules (EPIC-004).
  {
    id: 'approvals',
    label: 'navigation.approvals',
    icon: 'fact_check',
    route: '/approvals',
    roles: ['planner', 'admin'],
  },
  {
    id: 'documents',
    label: 'navigation.documents',
    icon: 'folder_shared',
    route: '/documents',
  },

  // ─── Planning section ─────────────────────────────────────────────────────
  {
    label: 'navigation.sectionPlanning',
    roles: ['planner', 'admin'],
    items: [
      {
        id: 'planning-periods',
        label: 'navigation.planningPeriods',
        icon: 'date_range',
        route: '/planning/periods',
        roles: ['planner', 'admin'],
      },
      {
        id: 'planning-plans',
        label: 'navigation.planningPlans',
        icon: 'assignment',
        route: '/planning/plans',
        roles: ['planner', 'admin'],
      },
      {
        id: 'planning-strategies',
        label: 'navigation.planningStrategies',
        icon: 'lightbulb',
        route: '/planning/strategies',
        roles: ['planner', 'admin'],
      },
      {
        id: 'planning-labour-pool',
        label: 'navigation.planningLabourPool',
        icon: 'groups',
        route: '/planning/labour-pool',
        roles: ['planner', 'admin'],
      },
      {
        id: 'shift-definitions',
        label: 'navigation.shiftDefinitions',
        icon: 'schedule',
        route: '/shift-definitions',
        roles: ['planner', 'admin'],
      },
    ],
  },

  ...(TIMETRACKING_MENU_ENABLED ? [TIME_TRACKING_SECTION] : []),
  ...(PROJECTS_MENU_ENABLED ? [PROJECTS_SECTION] : []),

  // ─── Organization section ─────────────────────────────────────────────────
  {
    label: 'navigation.sectionOrganization',
    roles: ['planner', 'admin'],
    items: [
      {
        id: 'persons',
        label: 'navigation.persons',
        icon: 'people',
        route: '/persons',
        roles: ['planner', 'admin'],
      },
      {
        id: 'teams',
        label: 'navigation.teams',
        icon: 'groups',
        route: '/teams',
        roles: ['planner', 'admin'],
      },
      {
        id: 'skills-catalog',
        label: 'navigation.skillsCatalog',
        icon: 'school',
        route: '/skills-catalog',
        roles: ['planner', 'admin'],
      },
      {
        id: 'locations',
        label: 'navigation.locations',
        icon: 'location_on',
        route: '/locations',
        roles: ['planner', 'admin'],
      },
      ...(REPORTS_MENU_ENABLED ? [REPORTS_ITEM] : []),
    ],
  },
];

/**
 * Maps the open-approvals count onto the badge of the `approvals` nav item
 * (story 34-1). A count of 0 leaves the entry badge-less. Pure function — the
 * layout applies it on the permission-filtered entries.
 */
export function applyApprovalsBadge(entries: NavConfigEntry[], openCount: number): NavConfigEntry[] {
  if (openCount <= 0) return entries;
  return entries.map(entry => (!isNavSection(entry) && entry.id === 'approvals' ? { ...entry, badge: openCount } : entry));
}

/** Access facts of the current user the nav filtering runs on (story 35-2). */
export interface NavFilterContext {
  hasRole(role: string): boolean;
  hasPermission(permission: string): boolean;
  hasAnyPermission(permissions: string[]): boolean;
  /** Whether the user is linked to a core.persons row (own time tracking). */
  hasLinkedPerson: boolean;
}

/**
 * Nav item id → required permission(s). An array means ANY of the listed
 * permissions unlocks the item; a missing entry shows the item to every
 * authenticated user.
 */
const NAV_PERMISSION_MAP: Record<string, string | string[]> = {
  dashboard: 'dashboard:read',
  calendars: 'calendars:read',
  documents: ['documents:read:own', 'documents:read', 'files:read'],
  'planning-periods': 'plans:read',
  'planning-plans': 'plans:read',
  'planning-strategies': 'strategies:read',
  'shift-definitions': 'shifts:read',
  persons: 'employees:read',
  'skills-catalog': 'skills:read',
  teams: 'teams:read',
  locations: 'locations:read',
  'my-plan': 'schedules:read:own',
  'my-schedule': 'schedules:read:own',
  'shift-applications': 'shift-applications:read',
  'shift-requests': ['shift-swaps:read', 'requests:read'],
  'planning-labour-pool': 'labour-pool:read',
  reports: ['reports:generate', 'plans:read'],
  'time-tracking-absences': ['settings:read', 'settings:update'],
  'time-tracking-balance-overview': 'employees:balances:read',
  'time-tracking-balances': ['settings:read', 'settings:update'],
  'time-tracking-my-bookings': ['timetracking:my-week:read', 'timetracking:manual-bookings:read'],
  approvals: 'timetracking:review-queue:read',
  'time-tracking-rules': ['settings:read', 'settings:update'],
  'projects-time': 'projects:my-time:read',
  'projects-admin': 'projects:read',
  'projects-activity-types': 'projects:read',
  'projects-cost-rates': 'projects:read',
  // Settings is handled separately via the showSettings input.
};

/**
 * Items coupled to OWN time tracking (story 35-2, U6): visible only with a
 * linked person — an admin without a person must not see a dead link.
 */
const LINKED_PERSON_ITEM_IDS = new Set(['time-tracking-my-bookings', 'projects-time']);

function isNavItemVisible(item: NavItem, context: NavFilterContext): boolean {
  // Role-gated items — the user must carry at least one of the listed roles.
  // Checked BEFORE permissions so wildcard-permission users (e.g. admin with
  // '*') stay excluded from role-specific features (e.g. 'my-plan').
  if (item.roles?.length && !item.roles.some(role => context.hasRole(role))) {
    return false;
  }

  if (LINKED_PERSON_ITEM_IDS.has(item.id) && !context.hasLinkedPerson) {
    return false;
  }

  const requiredPermission = NAV_PERMISSION_MAP[item.id];
  if (!requiredPermission || requiredPermission === '') {
    return true;
  }
  if (Array.isArray(requiredPermission)) {
    return context.hasAnyPermission(requiredPermission);
  }
  return context.hasPermission(requiredPermission);
}

function stripRoles<T extends NavItem>(item: T): T {
  const { roles: _, ...itemWithoutRoles } = item;
  return itemWithoutRoles as T;
}

/**
 * Filters the nav config by the user's access facts (story 35-2). Pure
 * function — the layout provides the context from its services. Sections are
 * kept when ≥1 item stays visible; `roles` are stripped after filtering so
 * the sidebar does not double-filter.
 */
export function filterNavEntries(entries: NavConfigEntry[], context: NavFilterContext): NavConfigEntry[] {
  const result: NavConfigEntry[] = [];

  for (const entry of entries) {
    if (isNavSection(entry)) {
      const filteredItems = entry.items.filter(item => isNavItemVisible(item, context)).map(stripRoles);
      if (filteredItems.length > 0) {
        const { roles: _, ...sectionWithoutRoles } = entry;
        result.push({ ...sectionWithoutRoles, items: filteredItems });
      }
    } else if (isNavItemVisible(entry, context)) {
      result.push(stripRoles(entry));
    }
  }

  return result;
}
